import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Label } from "../shadcn/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../shadcn/ui/card";
import { Calendar, Filter, CheckCircle2, XCircle, AlertCircle, Circle } from "lucide-react";

// Property Type Button Component - Similar to IllustrativeFilterDialog
const PropertyTypeButton = ({ label, selected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium transition-all duration-200 ${
        selected
          ? "ring-1 ring-gray-900 text-black bg-white"
          : "ring-1 ring-gray-200 text-black bg-white hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );
};

const BookingFilterDialog = ({ isOpen, onClose, onApplyFilters, onClearFilters, initialFilters = {} }) => {
  // Use same filter structure as Booking.jsx
  const [filterState, setFilterState] = useState({
    status: initialFilters.status || "ALL",
    timeFilter: initialFilters.timeFilter || "all", // "upcoming", "active", "past", "all"
  });

  // Update filter state when initialFilters change
  useEffect(() => {
    setFilterState({
      status: initialFilters.status || "ALL",
      timeFilter: initialFilters.timeFilter || "all",
    });
  }, [initialFilters]);

  const handleApplyFilters = () => {
    onApplyFilters(filterState);
    onClose();
  };

  const handleClearFilters = () => {
    const resetFilters = {
      status: "ALL",
      timeFilter: "all",
    };
    setFilterState(resetFilters);
    onClearFilters();
  };

  const statusOptions = [
    { value: "ALL", label: "All Status" },
    { value: "PENDING", label: "Pending" },
    { value: "PENDING_PAYMENT", label: "Pending Payment" },
    { value: "CHECKED_IN", label: "Checked In" },
    { value: "CANCELED", label: "Canceled" },
    { value: "COMPLETED", label: "Completed" },
    { value: "NO_SHOW", label: "No Show" },
  ];

  const timeFilterOptions = [
    { value: "all", label: "All Reservations" },
    { value: "upcoming", label: "Upcoming" },
    { value: "active", label: "Active" },
    { value: "past", label: "Past" },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-4 pb-3 flex-shrink-0">
          <DialogTitle className="text-lg font-semibold text-gray-900">Filters</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-3 py-2">
            {/* Status Section */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Filter className="w-3 h-3" />
                  Status
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map(option => (
                    <PropertyTypeButton
                      key={option.value}
                      label={option.label}
                      selected={filterState.status === option.value}
                      onClick={() => {
                        setFilterState(prev => ({ ...prev, status: option.value }));
                      }}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Date/Time Filter Section */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  Date & Time
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 mb-2 block">Filter by reservation timing</Label>
                  <div className="flex flex-wrap gap-2">
                    {timeFilterOptions.map(option => (
                      <PropertyTypeButton
                        key={option.value}
                        label={option.label}
                        selected={filterState.timeFilter === option.value}
                        onClick={() => {
                          setFilterState(prev => ({ ...prev, timeFilter: option.value }));
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {filterState.timeFilter === "upcoming" && "Shows reservations that haven't started yet"}
                    {filterState.timeFilter === "active" && "Shows reservations currently in progress"}
                    {filterState.timeFilter === "past" && "Shows past reservations"}
                    {filterState.timeFilter === "all" && "Shows all reservations"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="flex justify-between px-6 py-4 bg-white border-t border-gray-200 flex-shrink-0">
          <Button variant="outline" onClick={handleClearFilters} className="px-6">
            Clear all
          </Button>
          <Button onClick={handleApplyFilters} className="px-6 bg-gray-900 hover:bg-gray-800">
            Apply filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BookingFilterDialog;
